import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, X } from "lucide-react";
import { createRoute, updateRoute } from "@/api/routes";
import { getPlanningSettings } from "@/api/settings";
import { getWarehouses } from "@/api/warehouses";
import { tenantTodayISO } from "@/lib/date";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import { cn } from "@/lib/cn";
import { ROUTE_COLORS } from "@/lib/mapIcons";
import { applyApiErrorToForm, type TranslateFn } from "@/lib/apiErrors";
import {
  dateFormatMessage,
  maxLengthMessage,
  requiredMessage,
} from "@/lib/zodMessages";
import type { CreateRoutePayload, RouteDetail } from "@/types/routes";
import { DateInput } from "@/components/ui/DateInput";
import { VehicleCombobox } from "./VehicleCombobox";

/**
 * Default route colour, used when the dispatcher never touches the swatches.
 * This is a ROUTE-DATA colour (the value stored on `Route.color` and drawn on
 * the map), NOT a theme colour — see the note on {@link SWATCH_COLORS}.
 */
const DEFAULT_ROUTE_COLOR = ROUTE_COLORS[0];

/**
 * The preset swatches offered under «Цвет».
 *
 * DEV_RULES §10.10 EXEMPTION — deliberate, do not "fix" these into brand
 * tokens. These hex values are *data the dispatcher picks*, not theme colours:
 * the chosen value is persisted on `Route.color` and later drawn as the route's
 * polyline/marker fill on the map. They are the first three entries of the
 * shared {@link ROUTE_COLORS} map palette precisely so a route created here
 * matches how it renders on the map. A dealer's brand palette must NOT change
 * what colour an existing route is drawn in, so these are correctly literal.
 */
const SWATCH_COLORS = ROUTE_COLORS.slice(0, 3);

/**
 * TASK-4747: "Name is required" / "Max 255 characters" / "Date must be
 * YYYY-MM-DD" were hardcoded English literals printed straight into a
 * fully-localized UI — the form renders `errors.<field>.message` verbatim, so
 * they never passed through i18next. Each now names the field using the SAME
 * key its visible `<label>` renders, so message and label can never drift.
 */
function buildSchema(t: TranslateFn) {
  const nameLabel = t("routes.routeName");
  const dateLabel = t("routes.date");

  return z.object({
    name: z
      .string()
      .min(1, requiredMessage(t, nameLabel))
      .max(255, maxLengthMessage(t, nameLabel, 255)),
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, dateFormatMessage(t, dateLabel)),
    color: z.string().nullable(),
    vehicle_id: z.string().optional(),
    warehouse_id: z.string().optional(),
    mid_warehouse_id: z.string().optional(),
    end_warehouse_id: z.string().optional(),
    visit_order: z.enum(["strict", "any"]),
    planned_mileage_km: z
      .union([z.number().min(0), z.nan()])
      .nullable()
      .optional(),
    planned_duration_min: z
      .union([z.number().min(0), z.nan()])
      .nullable()
      .optional(),
  });
}

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

/**
 * TASK-4908: ONE component serves «Новый маршрут» and «Редактировать».
 *
 * The two used to be `CreateRouteModal` (656 lines) and `EditRouteModal` (453)
 * — the same form twice, which is exactly the drift TASK-4832 had to undo for
 * the template modals. The mode is a discriminated prop rather than two
 * components sharing a body, because everything that differs between them
 * (defaults, mutation, post-submit behaviour) is *state*, not markup: a shared
 * "body" component would have had to take all of it back as props anyway.
 *
 * Everything below that reads `props.mode` is a genuine behavioural difference;
 * the markup itself is mode-agnostic apart from the create-only
 * «Открыть планирование точек» card.
 */
type Props =
  | {
      mode: "create";
      open: boolean;
      onOpenChange: (open: boolean) => void;
      /** When provided, called with the new route ID instead of navigating to the route page. */
      onSuccess?: (routeId: number) => void;
      route?: never;
    }
  | {
      mode: "edit";
      open: boolean;
      onOpenChange: (open: boolean) => void;
      /** The route being edited; every field is prefilled from it. */
      route: RouteDetail;
      onSuccess?: never;
    };

/**
 * The third state of a warehouse select (BUG-542).
 *
 * A warehouse slot is three-valued, and the empty string cannot carry two of those
 * meanings at once:
 *
 *   INHERIT ("")            → the caller expresses no opinion; the key is OMITTED from the
 *                             payload and the backend substitutes the tenant default.
 *   NONE (this sentinel)    → «Нет склада», an explicit choice; sent as `null` and honoured
 *                             as "build without this depot" (BUG-541).
 *   "<id>"                  → that specific warehouse.
 *
 * Conflating the first two is the whole of BUG-542: the modal always sent `null`, so the
 * backend's "field absent → use the tenant default" branch was unreachable and the company
 * default never applied. The value is a string because these are native <select>s, and it
 * is deliberately not a plausible id.
 */
const WAREHOUSE_NONE = "__none__";

/** Shared input/select chrome so every control on the form matches. */
const FIELD_CLASS =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-60";

/** Uppercase column heading («МАРШРУТ» / «СКЛАДЫ И ПОРЯДОК»). */
const SECTION_CLASS =
  "mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400";

const LABEL_CLASS = "mb-1.5 block text-sm font-semibold text-gray-900";

const HINT_CLASS = "mt-2 text-xs leading-relaxed text-gray-500";

/**
 * Blank form state — the create-mode defaults, also used to reset on close.
 *
 * BUG-530: `tz` is threaded in rather than read here, because this is a
 * module-level helper and `useTenantTimezone()` is a hook — it cannot be called
 * outside a component. The date must be «today» in the TENANT's timezone: the
 * old `getTodayISO()` sliced a UTC instant, so for Europe/Minsk (UTC+3) a route
 * created between 00:00 and 03:00 local was SUBMITTED dated to yesterday.
 * Edit mode is untouched — it prefills from `valuesFromRoute(route)`.
 *
 * @param tz IANA timezone name from `useTenantTimezone()`.
 */
function emptyValues(tz: string): FormValues {
  return {
    name: "",
    date: tenantTodayISO(tz),
    color: DEFAULT_ROUTE_COLOR,
    vehicle_id: "",
    warehouse_id: "",
    mid_warehouse_id: "",
    end_warehouse_id: "",
    visit_order: "strict",
    planned_mileage_km: null,
    planned_duration_min: null,
  };
}

/** Edit-mode prefill: every writable field read back off the route. */
function valuesFromRoute(route: RouteDetail): FormValues {
  return {
    name: route.name,
    date: route.date,
    color: route.color ?? DEFAULT_ROUTE_COLOR,
    vehicle_id: route.vehicle?.id ? String(route.vehicle.id) : "",
    /**
     * BUG-542: on EDIT there is nothing left to inherit — the route already has its
     * warehouses resolved. A slot that is empty on a saved route means the route has no
     * such depot, which is NONE, not INHERIT. Mapping it to INHERIT would make an edit
     * silently re-apply the tenant default to a route the dispatcher had cleared.
     */
    warehouse_id: route.warehouse?.id
      ? String(route.warehouse.id)
      : WAREHOUSE_NONE,
    mid_warehouse_id: route.mid_warehouse?.id
      ? String(route.mid_warehouse.id)
      : WAREHOUSE_NONE,
    end_warehouse_id: route.end_warehouse?.id
      ? String(route.end_warehouse.id)
      : WAREHOUSE_NONE,
    visit_order: route.visit_order ?? "strict",
    /**
     * COERCED, not passed through. `planned_mileage_km` is a DRF decimal and
     * arrives as the STRING `"18.59"`, while the number input's `setValueAs`
     * produces a `Number`. Prefilling the raw string made `18.59 !== "18.59"`
     * true on every submit, so the PATCH-only-what-changed diff below reported
     * this field as dirty even when the dispatcher never touched it — every
     * edit re-sent the mileage.
     *
     * The existing test did not catch it because its fixture used the number
     * `120`, matching the type this field WRONGLY claimed rather than what the
     * server actually sends.
     */
    planned_mileage_km: toFormNumber(route.planned_mileage_km),
    planned_duration_min: route.planned_duration_min ?? null,
  };
}

/**
 * Normalizes a serialized numeric field to what the form holds: a `number`, or
 * `null` when absent/unparseable. Decimal fields cross the wire as strings.
 */
function toFormNumber(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : parseFloat(value);
  return isFinite(n) ? n : null;
}

export function RouteFormModal(props: Props) {
  const { mode, open, onOpenChange } = props;
  const isEdit = mode === "edit";
  const route = props.mode === "edit" ? props.route : null;

  /**
   * A route is only editable while it is still `planned` — carried over from
   * the previous edit modal. Create is never read-only.
   */
  const isReadOnly = route ? route.status !== "planned" : false;

  const { t } = useTranslation();
  const tenantTz = useTenantTimezone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /**
   * «Открыть планирование точек» — when checked, the dispatcher is taken to the
   * route page after creating. CREATE-ONLY: "go to the route after creating"
   * is meaningless while editing a route you are already looking at, so the
   * card is not rendered in edit mode and this state is never read there.
   */
  const [openPlanning, setOpenPlanning] = useState(false);

  // Schema is built from `t` so validation messages are localized; memoized on
  // `t` so the resolver identity is stable across renders and only changes when
  // the active language does.
  const schema = useMemo(() => buildSchema(t), [t]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: route ? valuesFromRoute(route) : emptyValues(tenantTz),
  });

  const colorValue = watch("color");
  const visitOrder = watch("visit_order");

  const { data: warehousesData, isLoading: warehousesLoading } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => getWarehouses(),
    enabled: open,
  });

  const { data: planningSettings } = useQuery({
    queryKey: ["planning-settings"],
    queryFn: getPlanningSettings,
    // BUG-542: needed in BOTH modes now. Create mode uses the defaults to label the
    // INHERIT option; edit mode needs `require_depot` to decide whether that option is
    // offered at all. It is still never used to OVERWRITE an existing route's warehouses
    // — `valuesFromRoute` maps an empty slot to NONE, never to INHERIT.
    enabled: open,
  });

  /**
   * BUG-542: the create form opens on INHERIT for all three slots.
   *
   * This used to `setValue` the start slot to the tenant's default id, and do nothing at
   * all for mid/end. Two problems with that: it only ever covered ONE of the three slots,
   * and pinning a concrete id is not the same as inheriting — a dispatcher who never
   * touched the control ended up sending that id explicitly, so a later change to the
   * company default would not apply to a route they thought they had left on "default".
   *
   * INHERIT (empty string) is already the form's initial value from `emptyValues`, so
   * there is nothing to set — the work here is now purely NOT overriding it. The effect
   * is kept only to reset the slots when a create form is reopened after an edit.
   */
  useEffect(() => {
    if (!open || isEdit) return;
    setValue("warehouse_id", "");
    setValue("mid_warehouse_id", "");
    setValue("end_warehouse_id", "");
  }, [open, isEdit, setValue]);

  /**
   * Field map for `applyApiErrorToForm`. BUG-464: the axios transport string
   * replaced the server's reason. Note the FK names differ across the boundary
   * — the backend sends `vehicle`/`warehouse` while the form fields are
   * `vehicle_id`/`warehouse_id`, which is precisely what this indirection is
   * for. Keys are BACKEND field names (the unsuffixed serializer names, see the
   * payload note in `buildPayload`); values are form fields.
   */
  const errorFieldMap = {
    name: "name",
    date: "date",
    color: "color",
    vehicle: "vehicle_id",
    warehouse: "warehouse_id",
    mid_warehouse: "mid_warehouse_id",
    end_warehouse: "end_warehouse_id",
    visit_order: "visit_order",
    planned_mileage_km: "planned_mileage_km",
    planned_duration_min: "planned_duration_min",
  } as const;

  /**
   * Both mutations invalidate the table list query (`["routes"]`) AND the map
   * query (`["routes-map"]`, RoutesPage). Without the map invalidation a new or
   * edited route keeps its stale state on the map until a manual refresh
   * (BUG-297 / BUG-335).
   */
  async function invalidateRouteQueries() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["routes"] }),
      queryClient.invalidateQueries({ queryKey: ["routes-map"] }),
    ]);
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateRoutePayload) => createRoute(data),
    onSuccess: async (result) => {
      await invalidateRouteQueries();
      onOpenChange(false);
      const onCreated = props.mode === "create" ? props.onSuccess : undefined;
      if (onCreated) {
        // Callers that own their post-create flow (DashboardTab,
        // UnassignedOrdersSlideOver) never navigate — they refresh their own
        // surface instead. Unchanged.
        onCreated(result.id);
      } else if (openPlanning) {
        // Only «Открыть планирование точек» navigates — and navigation is ALL
        // it does. The route detail page with its map is the destination; the
        // dispatcher decides what to do next there. Nothing is auto-opened on
        // arrival (no location state is passed), so a reload of that page has
        // no leftover flag to act on either.
        navigate(`/routes/${result.id}`);
      }
      // Unchecked: stay on the routes list. The invalidations above already
      // refetched it (the list query key is prefixed `["routes"]`), so the new
      // route appears without a manual refresh — no navigation needed.
    },
    onError: (error: unknown) => {
      applyApiErrorToForm(error, t, setError, errorFieldMap);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateRoutePayload>) =>
      // `route` is non-null on every path that can reach this mutation (edit
      // mode only); the assertion keeps `mutationFn` free of a dead branch.
      updateRoute(route!.id, data),
    onSuccess: async () => {
      await invalidateRouteQueries();
      // Edit stays where it is and just closes — no navigation, no callback.
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      applyApiErrorToForm(error, t, setError, errorFieldMap);
    },
  });

  const mutation = isEdit ? updateMutation : createMutation;

  /**
   * Reset the form when the modal closes, and prefill it in edit mode.
   *
   * The prefill WAITS for `warehousesData`. The three warehouse controls are
   * native <select>s whose <option>s come from that query: resetting before the
   * options exist leaves the browser with no matching option, so it snaps the
   * select back to "" and the route's saved warehouses silently vanish from the
   * form — after which saving would clear them on the server. Gating on the
   * loaded list is what makes the prefill deterministic rather than a race the
   * fast local network happens to win.
   */
  useEffect(() => {
    if (open) {
      if (route && warehousesData) reset(valuesFromRoute(route));
      return;
    }
    reset(emptyValues(tenantTz));
    setOpenPlanning(false);
  }, [open, route, warehousesData, reset, tenantTz]);

  /**
   * BUG-530: re-seed the CREATE-mode date when the modal opens.
   *
   * The `useForm` default above is captured once at mount, and this modal is
   * mounted (closed) by its host — so by the time the dispatcher opens it, that
   * date may have been derived from the `Europe/Minsk` fallback while
   * `["settings-planning"]` was still loading, or simply gone stale in a tab
   * left open past the tenant's midnight.
   *
   * Three guards keep this from touching anything it should not:
   *   - `isEdit` short-circuit: the edit path prefills from the ROUTE and must
   *     never be re-dated from the clock.
   *   - the ref: fires once per open, so a late `tenantTz` resolution cannot
   *     overwrite a date the dispatcher has already picked.
   *   - `setValue` on the single `date` field rather than a whole-form `reset`,
   *     so a name already typed into the open form survives.
   */
  const dateSeededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      dateSeededRef.current = false;
      return;
    }
    if (isEdit || dateSeededRef.current) return;
    dateSeededRef.current = true;
    setValue("date", tenantTodayISO(tenantTz));
  }, [open, isEdit, setValue, tenantTz]);

  /**
   * Builds the wire payload from form values.
   *
   * The three warehouse keys are `warehouse` / `mid_warehouse` /
   * `end_warehouse` — NOT `*_id`. `RouteCreateUpdateSerializer` (the create AND
   * partial_update serializer, so this holds for POST and PATCH alike) declares
   * them as PrimaryKeyRelatedFields under exactly these names. DRF drops
   * unknown keys silently, so the `*_id` spelling returns 201/200 and saves NO
   * warehouse at all — including the start one. The `*_id` form is correct only
   * for the build endpoint (`BuildRoutePayload`). The `*_id` names inside
   * `FormValues` are react-hook-form field names and never reach the wire.
   * Verified live.
   */
  /**
   * Map one three-valued warehouse select onto the wire (BUG-542).
   *
   * Returns `{}` for INHERIT so the key is genuinely ABSENT from the JSON — that absence
   * is the signal `perform_create` tests to apply the tenant default. Returning `null`
   * here instead is precisely the bug: DRF sees an explicit null, honours it as «нет
   * склада», and the company setting never applies.
   */
  function warehouseKey(
    key: "warehouse" | "mid_warehouse" | "end_warehouse",
    value: string | undefined,
  ): Partial<CreateRoutePayload> {
    if (value === undefined || value === "") return {};
    if (value === WAREHOUSE_NONE)
      return { [key]: null } as Partial<CreateRoutePayload>;
    return { [key]: Number(value) } as Partial<CreateRoutePayload>;
  }

  function buildPayload(values: FormValues): CreateRoutePayload {
    return {
      name: values.name,
      date: values.date,
      color: values.color ?? DEFAULT_ROUTE_COLOR,
      vehicle: values.vehicle_id ? Number(values.vehicle_id) : null,
      ...warehouseKey("warehouse", values.warehouse_id),
      ...warehouseKey("mid_warehouse", values.mid_warehouse_id),
      ...warehouseKey("end_warehouse", values.end_warehouse_id),
      visit_order: values.visit_order,
      planned_mileage_km:
        values.planned_mileage_km != null &&
        !isNaN(values.planned_mileage_km as number)
          ? (values.planned_mileage_km as number)
          : null,
      planned_duration_min:
        values.planned_duration_min != null &&
        !isNaN(values.planned_duration_min as number)
          ? (values.planned_duration_min as number)
          : null,
    };
  }

  function onSubmit(values: FormValues) {
    const payload = buildPayload(values);

    if (!route) {
      createMutation.mutate(payload);
      return;
    }

    // PATCH sends only what actually changed — an untouched field is left out
    // of the request entirely rather than re-sent with its current value.
    const original = buildPayload(valuesFromRoute(route));
    const patch: Partial<CreateRoutePayload> = {};
    // Union of both key sets, not just `payload`'s (BUG-542). A warehouse slot set to
    // INHERIT produces NO key at all, so iterating `payload` alone would silently skip a
    // slot that had a value before and does not now. Edit mode never prefills INHERIT, so
    // this cannot currently arise — but a diff that depends on that invariant holding
    // elsewhere in the file is the kind of thing that breaks quietly later.
    const keys = new Set<keyof CreateRoutePayload>([
      ...(Object.keys(payload) as (keyof CreateRoutePayload)[]),
      ...(Object.keys(original) as (keyof CreateRoutePayload)[]),
    ]);
    for (const key of keys) {
      if (payload[key] !== original[key]) {
        // Every CreateRoutePayload member is a primitive or null, so a
        // key-wise !== is a complete diff here.
        patch[key] = payload[key] as never;
      }
    }

    if (Object.keys(patch).length === 0) {
      // Nothing changed: skip the request and just close, so an accidental
      // open/save is not a pointless write (and cannot bump `updated_at`).
      onOpenChange(false);
      return;
    }

    updateMutation.mutate(patch);
  }

  /**
   * One row of the grouped warehouse card. Each row is a label + a native
   * <select> stretched invisibly across the row, so the whole row is clickable
   * while the visible text stays right-aligned with a chevron, per the mockup.
   */
  function warehouseRow(
    field: "warehouse_id" | "mid_warehouse_id" | "end_warehouse_id",
    labelKey: string,
    dotClass: string,
  ) {
    const value = watch(field) ?? "";
    const selected = warehousesData?.results.find(
      (w) => String(w.id) === value,
    );
    /**
     * BUG-542: three states, and the INHERIT row names the warehouse it would inherit
     * («Из настроек компании (Офис)») so the dispatcher can see what they are agreeing
     * to without opening settings. The tenant defaults are only offerable when
     * `require_depot` is on — with the switch off there is nothing to inherit, so the
     * option is dropped and the control opens on «Нет склада».
     */
    const inheritedWarehouse =
      field === "warehouse_id"
        ? planningSettings?.default_start_warehouse
        : field === "mid_warehouse_id"
          ? planningSettings?.default_mid_warehouse
          : planningSettings?.default_end_warehouse;
    const canInherit = planningSettings?.require_depot === true;
    const inheritLabel = inheritedWarehouse?.name
      ? t("routes.form.warehouseFromSettingsNamed", {
          name: inheritedWarehouse.name,
        })
      : t("routes.form.warehouseFromSettings");
    const displayName = warehousesLoading
      ? t("routes.loading")
      : selected
        ? selected.name
        : value === WAREHOUSE_NONE || !canInherit
          ? t("routes.form.noWarehouse")
          : inheritLabel;
    return (
      <div className="relative flex items-center gap-2.5 px-3.5 py-2.5">
        <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClass)} />
        <span className="text-sm font-semibold text-gray-900">
          {t(labelKey)}
        </span>
        <span className="ml-auto flex items-center gap-1.5 text-sm text-gray-400">
          <span className="max-w-[9rem] truncate">{displayName}</span>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <select
          {...register(field)}
          disabled={warehousesLoading || isReadOnly}
          aria-label={t(labelKey)}
          className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        >
          {canInherit && <option value="">{inheritLabel}</option>}
          <option value={WAREHOUSE_NONE}>{t("routes.form.noWarehouse")}</option>
          {warehousesData?.results.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-xl focus:outline-none">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-gray-100 px-7 py-5">
            <div>
              <div className="flex items-center gap-3">
                <Dialog.Title className="text-xl font-bold text-gray-900">
                  {isEdit ? t("routes.editRoute") : t("routes.newRoute")}
                </Dialog.Title>
                {route?.is_circular && (
                  <span className="inline-flex items-center rounded-full bg-brand-success/10 px-2.5 py-0.5 text-xs font-semibold text-brand-success">
                    {t("routes.circularRoute")}
                  </span>
                )}
              </div>
              <Dialog.Description className="mt-1 text-sm text-gray-500">
                {isEdit
                  ? t("routes.form.editSubtitle")
                  : t("routes.form.subtitle")}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t("common.closeDialog")}
                className="-mr-1.5 -mt-0.5 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* noValidate: zod owns validation (BUG-524). Native HTML5 constraint
              validation runs BEFORE React and, when it fails, silently aborts the
              submit — no event fires, so the app cannot render an error and the
              button just looks dead. Letting zod decide means an invalid value
              always produces a VISIBLE field error. */}
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="px-7 py-6">
              {/* Read-only notice for non-planned routes */}
              {isReadOnly && (
                <div className="mb-5 rounded-lg border border-brand-warning/30 bg-brand-warning/10 px-4 py-3 text-sm text-brand-warning">
                  {t("routes.editReadOnlyNotice")}
                </div>
              )}

              {/* API error banner */}
              {errors.root && (
                <div className="mb-5 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-4 py-3 text-sm text-brand-danger">
                  {errors.root.message}
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                {/* ---------------- LEFT: МАРШРУТ ---------------- */}
                <div>
                  <h3 className={SECTION_CLASS}>
                    {t("routes.form.sectionRoute")}
                  </h3>

                  {/* Name */}
                  <div>
                    {/* The visible label is the mockup's short «Название»; the
                        zod message keeps using `routes.routeName` so the
                        validation sentence still names the field unambiguously
                        outside the modal's own context (TASK-4747). */}
                    <label className={LABEL_CLASS} htmlFor="route-form-name">
                      {t("routes.form.nameLabel")}{" "}
                      <span className="text-brand-danger">*</span>
                    </label>
                    <input
                      id="route-form-name"
                      {...register("name")}
                      disabled={isReadOnly}
                      placeholder={t("routes.form.namePlaceholder")}
                      className={FIELD_CLASS}
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-brand-danger">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Date + Color, side by side */}
                  <div className="mt-5 flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <label className={LABEL_CLASS}>
                        {t("routes.date")}{" "}
                        <span className="text-brand-danger">*</span>
                      </label>
                      <Controller
                        control={control}
                        name="date"
                        render={({ field }) => (
                          <DateInput
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            disabled={isReadOnly}
                            aria-label={t("routes.date")}
                            className="w-full py-2.5"
                          />
                        )}
                      />
                      {errors.date && (
                        <p className="mt-1 text-xs text-brand-danger">
                          {errors.date.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={LABEL_CLASS}>{t("routes.color")}</label>
                      <div className="flex items-center gap-2">
                        {SWATCH_COLORS.map((swatch) => {
                          const active =
                            (colorValue ?? "").toLowerCase() ===
                            swatch.toLowerCase();
                          return (
                            <button
                              key={swatch}
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => setValue("color", swatch)}
                              aria-label={swatch}
                              aria-pressed={active}
                              className={cn(
                                "h-9 w-9 rounded-lg transition-shadow disabled:cursor-not-allowed disabled:opacity-60",
                                active &&
                                  "ring-2 ring-offset-2 ring-brand-primary",
                              )}
                              // Dynamic hex swatch — the one permitted inline
                              // style. Route colour is DATA (persisted on
                              // Route.color, drawn on the map), not a theme
                              // colour, so no Tailwind token can express it.
                              style={{ backgroundColor: swatch }}
                            />
                          );
                        })}
                        {/* Custom colour: the native picker, kept from the
                            previous implementation per the acceptance criteria. */}
                        <label
                          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-400 transition-colors hover:border-gray-400 hover:text-gray-600"
                          title={t("routes.pickColor")}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          <input
                            type="color"
                            value={colorValue ?? DEFAULT_ROUTE_COLOR}
                            disabled={isReadOnly}
                            onChange={(e) => setValue("color", e.target.value)}
                            className="sr-only"
                            aria-label={t("routes.pickColor")}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle */}
                  <div className="mt-5">
                    <label className={LABEL_CLASS}>
                      {t("routes.form.vehicleLabel")}
                    </label>
                    <VehicleCombobox
                      value={watch("vehicle_id") ?? ""}
                      onChange={(id) => setValue("vehicle_id", id)}
                      disabled={isReadOnly}
                      /* TASK-4912: the route we are editing already carries its
                         vehicle, so the field can render «Renault Master · A
                         1234-7» on the first paint. Without this the combobox's
                         `enabled: open` gate leaves it showing the raw id until
                         the dispatcher opens the dropdown. Null in create mode,
                         where there is no preselection to label. */
                      initialVehicle={route?.vehicle ?? null}
                      // The visible <label> above is not associated (no htmlFor/id),
                      // so the control needs its own accessible name.
                      aria-label={t("routes.form.vehicleLabel")}
                    />
                    <p className={HINT_CLASS}>{t("routes.form.vehicleHint")}</p>
                  </div>

                  {/* Planned mileage + planned duration, side by side */}
                  <div className="mt-5 flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <label
                        className={LABEL_CLASS}
                        htmlFor="route-form-mileage"
                      >
                        {t("routes.form.plannedMileageShort")}
                      </label>
                      <div className="relative">
                        <input
                          id="route-form-mileage"
                          {...register("planned_mileage_km", {
                            setValueAs: (v) =>
                              v === "" || v === null ? null : Number(v),
                          })}
                          type="number"
                          min="0"
                          /* BUG-524: the model stores DecimalField(decimal_places=2), so a
                             real route can hold 26.21. With step="0.1" the browser's native
                             constraint validation rejected that value and aborted submit
                             BEFORE React saw it — no submit event, no PATCH, no error shown,
                             and EVERY other field on the form silently failed to save too. */
                          step="0.01"
                          disabled={isReadOnly}
                          placeholder="—"
                          className={cn(FIELD_CLASS, "pr-11")}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          {t("routes.form.unitKm")}
                        </span>
                      </div>
                      {errors.planned_mileage_km && (
                        <p className="mt-1 text-xs text-brand-danger">
                          {errors.planned_mileage_km.message}
                        </p>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <label
                        className={LABEL_CLASS}
                        htmlFor="route-form-duration"
                      >
                        {t("routes.form.plannedDurationShort")}
                      </label>
                      <div className="relative">
                        <input
                          id="route-form-duration"
                          {...register("planned_duration_min", {
                            setValueAs: (v) =>
                              v === "" || v === null ? null : Number(v),
                          })}
                          type="number"
                          min="0"
                          disabled={isReadOnly}
                          placeholder="—"
                          className={cn(FIELD_CLASS, "pr-12")}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          {t("routes.form.unitMin")}
                        </span>
                      </div>
                      {errors.planned_duration_min && (
                        <p className="mt-1 text-xs text-brand-danger">
                          {errors.planned_duration_min.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ------------ RIGHT: СКЛАДЫ И ПОРЯДОК ------------ */}
                <div>
                  <h3 className={SECTION_CLASS}>
                    {t("routes.form.sectionWarehouses")}
                  </h3>

                  {/* Three warehouses as ONE grouped card */}
                  <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                    {warehouseRow(
                      "warehouse_id",
                      "routes.form.startWarehouseShort",
                      "bg-brand-success",
                    )}
                    {warehouseRow(
                      "mid_warehouse_id",
                      "routes.form.midWarehouseShort",
                      "bg-gray-300",
                    )}
                    {warehouseRow(
                      "end_warehouse_id",
                      "routes.form.endWarehouseShort",
                      "bg-brand-danger",
                    )}
                  </div>
                  <p className={HINT_CLASS}>
                    {t("routes.form.warehouseDefaultsHint")}
                  </p>

                  {/* Visit order — two-way segmented control */}
                  <div className="mt-5">
                    <label className={LABEL_CLASS}>
                      {t("routes.visitOrder.label")}
                    </label>
                    <div
                      role="radiogroup"
                      aria-label={t("routes.visitOrder.label")}
                      className="flex gap-1 rounded-xl bg-gray-100 p-1"
                    >
                      {(["strict", "any"] as const).map((option) => {
                        const active = visitOrder === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={isReadOnly}
                            onClick={() => setValue("visit_order", option)}
                            className={cn(
                              "flex-1 rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                              active
                                ? "bg-white font-semibold text-gray-900 shadow-sm"
                                : "font-medium text-gray-500 hover:text-gray-700",
                            )}
                          >
                            {t(`routes.visitOrder.${option}`)}
                          </button>
                        );
                      })}
                    </div>
                    <p className={HINT_CLASS}>
                      {visitOrder === "strict"
                        ? t("routes.visitOrder.strictHint")
                        : t("routes.visitOrder.anyHint")}
                    </p>
                  </div>

                  {/* Open stop planning — tinted card. CREATE-ONLY: it means
                      "go to the route after creating", which is meaningless
                      when the dispatcher is already editing that route. */}
                  {!isEdit && (
                    <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl bg-gray-50 p-4">
                      <input
                        type="checkbox"
                        checked={openPlanning}
                        onChange={(e) => setOpenPlanning(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-brand-primary"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">
                          {t("routes.form.openStopPlanning")}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                          {t("routes.form.openStopPlanningHint")}
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            {/* No «draft is saved automatically» line: nothing persists a draft
                here, and promising a recovery that does not exist invites the
                dispatcher to close the dialog and lose their input. Removed on
                the owner's instruction — do not re-add the text without
                building real autosave behind it. */}
            <div className="flex items-center justify-end gap-4 rounded-b-2xl border-t border-gray-100 bg-gray-50/60 px-7 py-4">
              <div className="flex gap-3">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    {t("routes.cancel")}
                  </button>
                </Dialog.Close>
                <button
                  type="submit"
                  disabled={mutation.isPending || isReadOnly}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {mutation.isPending && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  {isEdit ? t("routes.saveChanges") : t("routes.createRoute")}
                </button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
